import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';

const ExecutivePrivilegesContext = createContext(null);

// Features that require unlocked privileges
export const RESTRICTED_FEATURES = {
  SCOUT_MANAGEMENT: 'scout_management',
  HIT_LIST_FULL: 'hit_list_full',
  PAYOUTS: 'payouts',
  EARNINGS: 'earnings',
  BULK_ACTIONS: 'bulk_actions',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  IMPORT_DATA: 'import_data',
};

// Limits for locked accounts
export const LOCKED_LIMITS = {
  MAX_HIT_LIST_ITEMS: 10,
  MAX_SCOUTS: 0,
  MAX_SAVED_SONGS: 20,
};

export function ExecutivePrivilegesProvider({ children }) {
  const { token, user } = useAuth();
  const [privilegesStatus, setPrivilegesStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStatus = async () => {
      if (!token || user?.role !== 'executive') {
        setLoading(false);
        return;
      }
      
      try {
        const data = await api.getExecutivePrivilegesStatus(token);
        setPrivilegesStatus(data);
      } catch (err) {
        console.error('Failed to fetch privileges status:', err);
        // Default to locked if we can't fetch status
        setPrivilegesStatus({
          privileges_unlocked: false,
          reviews_completed: 0,
          reviews_required: 100,
          progress_percentage: 0,
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatus();
  }, [token, user]);

  const isFeatureUnlocked = (feature) => {
    if (!privilegesStatus) return false;
    if (privilegesStatus.privileges_unlocked) return true;
    
    // Some features are always available even when locked
    const alwaysAvailable = ['review_submissions', 'view_dashboard', 'update_profile'];
    if (alwaysAvailable.includes(feature)) return true;
    
    return false;
  };

  const getLimit = (limitKey) => {
    if (!privilegesStatus) return LOCKED_LIMITS[limitKey] || 0;
    if (privilegesStatus.privileges_unlocked) return Infinity;
    return LOCKED_LIMITS[limitKey] || 0;
  };

  const value = {
    privilegesStatus,
    loading,
    isUnlocked: privilegesStatus?.privileges_unlocked || false,
    isFoundingAR: privilegesStatus?.is_founding_ar || privilegesStatus?.unlock_method === 'founding_ar' || false,
    reviewsCompleted: privilegesStatus?.reviews_completed || 0,
    reviewsRequired: privilegesStatus?.reviews_required || 100,
    reviewsRemaining: privilegesStatus?.reviews_remaining || 100,
    progressPercentage: privilegesStatus?.progress_percentage || 0,
    isFeatureUnlocked,
    getLimit,
    refetch: async () => {
      if (!token) return;
      try {
        const data = await api.getExecutivePrivilegesStatus(token);
        setPrivilegesStatus(data);
      } catch (err) {
        console.error('Failed to refetch privileges status:', err);
      }
    }
  };

  return (
    <ExecutivePrivilegesContext.Provider value={value}>
      {children}
    </ExecutivePrivilegesContext.Provider>
  );
}

export function useExecutivePrivileges() {
  const context = useContext(ExecutivePrivilegesContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      privilegesStatus: null,
      loading: true,
      isUnlocked: false,
      isFoundingAR: false,
      reviewsCompleted: 0,
      reviewsRequired: 100,
      reviewsRemaining: 100,
      progressPercentage: 0,
      isFeatureUnlocked: () => false,
      getLimit: () => 0,
      refetch: () => {},
    };
  }
  return context;
}

export default ExecutivePrivilegesContext;
